import React, { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import "./AdminFluxopayWebhookHealth.css";

const STATUS = {
  healthy: {
    label: "Operação saudável",
    description: "Os webhooks estão sendo processados normalmente.",
  },
  attention: {
    label: "Requer atenção",
    description: "Existem eventos aguardando processamento.",
  },
  degraded: {
    label: "Operação degradada",
    description: "Existem falhas ou webhooks atrasados.",
  },
};

const dateTime = value => {
  if (!value) return "Não registrado";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value));
};

const duration = value => {
  if (value === null || value === undefined) return "Sem dados";

  if (Number(value) < 1000) {
    return `${Math.round(Number(value))} ms`;
  }

  return `${(Number(value) / 1000).toFixed(2)} s`;
};

const errorMessage = error => {
  const data = error?.response?.data;

  if (typeof data?.message === "string") return data.message;
  if (typeof error?.message === "string") return error.message;

  return "Não foi possível consultar a saúde dos webhooks.";
};

function MetricCard({ label, value, detail, tone = "neutral" }) {
  return (
    <article className={`fxpay-webhook-metric is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </article>
  );
}

export default function AdminFluxopayWebhookHealth() {
  const [hours, setHours] = useState(24);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        "/api/admin/fluxopay/webhook-health",
        {
          params: { hours },
        }
      );

      setHealth(response.data);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    load();
  }, [load]);

  const status = STATUS[health?.status] || STATUS.attention;
  const summary = health?.summary || {};
  const performance = health?.performance || {};
  const activity = health?.last_activity || {};
  const failures = health?.recent_failures || [];
  const failuresByType = health?.failures_by_type || [];

  return (
    <section className="fxpay-webhook-health">
      <header className="fxpay-webhook-health__header">
        <div>
          <span className="fxpay-webhook-health__eyebrow">
            MONITORAMENTO OPERACIONAL
          </span>

          <h2>Saúde dos webhooks</h2>

          <p>
            Acompanhe o recebimento e processamento das notificações enviadas
            pelo provedor.
          </p>
        </div>

        <div className="fxpay-webhook-health__actions">
          <label>
            Período
            <select
              value={hours}
              onChange={event => setHours(Number(event.target.value))}
              disabled={loading}
            >
              <option value={1}>Última hora</option>
              <option value={6}>Últimas 6 horas</option>
              <option value={24}>Últimas 24 horas</option>
              <option value={72}>Últimos 3 dias</option>
              <option value={168}>Últimos 7 dias</option>
            </select>
          </label>

          <button type="button" onClick={load} disabled={loading}>
            {loading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      {error && (
        <div className="fxpay-webhook-health__error">
          <strong>Falha ao consultar os webhooks.</strong>
          <span>{error}</span>
        </div>
      )}

      {!error && loading && !health && (
        <div className="fxpay-webhook-health__loading">
          Calculando a saúde dos webhooks...
        </div>
      )}

      {health && (
        <>
          <div
            className={`fxpay-webhook-health__status is-${health.status}`}
          >
            <div className="fxpay-webhook-health__status-icon">
              {health.status === "healthy" ? "✓" : "!"}
            </div>

            <div>
              <strong>{status.label}</strong>
              <span>{status.description}</span>
            </div>

            <small>
              Taxa de sucesso:{" "}
              <b>{Number(summary.success_rate || 0).toFixed(2)}%</b>
            </small>
          </div>

          <div className="fxpay-webhook-health__metrics">
            <MetricCard
              label="Recebidos"
              value={summary.total || 0}
              detail="No período selecionado"
            />

            <MetricCard
              label="Processados"
              value={summary.processed || 0}
              detail="Concluídos pelo sistema"
              tone="success"
            />

            <MetricCard
              label="Pendentes"
              value={summary.pending || 0}
              detail="Aguardando processamento"
              tone={summary.pending > 0 ? "warning" : "neutral"}
            />

            <MetricCard
              label="Atrasados"
              value={summary.delayed || 0}
              detail="Pendentes há mais de 5 minutos"
              tone={summary.delayed > 0 ? "danger" : "neutral"}
            />

            <MetricCard
              label="Falhas"
              value={summary.failed || 0}
              detail="Com erro de processamento"
              tone={summary.failed > 0 ? "danger" : "neutral"}
            />
          </div>

          <div className="fxpay-webhook-health__grid">
            <article className="fxpay-webhook-health__card">
              <header>
                <div>
                  <small>DESEMPENHO</small>
                  <h3>Tempo de processamento</h3>
                </div>
              </header>

              <dl className="fxpay-webhook-health__details">
                <div>
                  <dt>Tempo médio</dt>
                  <dd>
                    {duration(performance.average_processing_ms)}
                  </dd>
                </div>

                <div>
                  <dt>Maior tempo</dt>
                  <dd>
                    {duration(performance.maximum_processing_ms)}
                  </dd>
                </div>

                <div>
                  <dt>Último recebido</dt>
                  <dd>{dateTime(activity.received_at)}</dd>
                </div>

                <div>
                  <dt>Último processado</dt>
                  <dd>{dateTime(activity.processed_at)}</dd>
                </div>
              </dl>
            </article>

            <article className="fxpay-webhook-health__card">
              <header>
                <div>
                  <small>FALHAS POR EVENTO</small>
                  <h3>Tipos que exigem atenção</h3>
                </div>
              </header>

              {failuresByType.length === 0 ? (
                <div className="fxpay-webhook-health__empty">
                  Nenhuma falha registrada no período.
                </div>
              ) : (
                <div className="fxpay-webhook-health__types">
                  {failuresByType.map(item => (
                    <div key={item.event_type}>
                      <span>{item.event_type}</span>
                      <strong>{item.total}</strong>
                    </div>
                  ))}
                </div>
              )}
            </article>
          </div>

          <article className="fxpay-webhook-health__card">
            <header>
              <div>
                <small>DIAGNÓSTICO</small>
                <h3>Falhas recentes</h3>
              </div>

              {activity.failed_at && (
                <span>
                  Última falha: {dateTime(activity.failed_at)}
                </span>
              )}
            </header>

            {failures.length === 0 ? (
              <div className="fxpay-webhook-health__empty">
                Nenhuma falha encontrada no período selecionado.
              </div>
            ) : (
              <div className="fxpay-webhook-health__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Recebido em</th>
                      <th>Evento</th>
                      <th>Provedor</th>
                      <th>Tentativas</th>
                      <th>Status</th>
                      <th>Erro</th>
                    </tr>
                  </thead>

                  <tbody>
                    {failures.map(item => (
                      <tr key={item.id}>
                        <td>{dateTime(item.created_at)}</td>
                        <td>
                          <strong>{item.event_type}</strong>
                          <small>{item.provider_event_id}</small>
                        </td>
                        <td>{item.provider}</td>
                        <td>{item.attempts || 0}</td>
                        <td>
                          <span className="fxpay-webhook-health__badge is-failed">
                            Falha
                          </span>
                        </td>
                        <td title={item.last_error || ""}>
                          <span className="fxpay-webhook-health__error-text">
                            {item.last_error || "Erro não detalhado"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        </>
      )}
    </section>
  );
}